import React, { useEffect, useState, useRef } from 'react';
import { ConnectWebSocketForOverlay, DisconnectWebSocket, SetSocketMessageHandler, ClearSocketMessageHandler } from './../../../utilities/BL/Zones';
import { StartSequence, RegisterTap, GetState } from './../../../utilities/BL/Sequence';

const SequenceFan = () => {
    // Leer screenId desde URL
    const queryParams = new URLSearchParams(window.location.search);
    const screenId = parseInt(queryParams.get('screenId') || 1, 10);

    const [connectionStatus, setConnectionStatus] = useState('idle');
    const [gameState, setGameState] = useState({
        phase: 'WAITING',
        sequence: [],
        playerStep: 0,
        isPlaying: false,
        matchId: ''
    });

    const [tappedPad, setTappedPad] = useState(null);
    const [isSending, setIsSending] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    
    const lastPhaseRef = useRef('WAITING');

    const customStyles = `
        :root {
            --pad0: #00ff41; /* Green */
            --pad1: #ff003c; /* Red */
            --pad2: #fffb00; /* Yellow */
            --pad3: #00e1ff; /* Blue */
            --pad4: #b300ff; /* Purple */
            --pad5: #ff8800; /* Orange */
            --bg-dark: #0a0a0f;
        }

        .sequence-fan-body {
            margin: 0;
            padding: 0;
            background-color: var(--bg-dark);
            height: 100vh;
            width: 100vw;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            font-family: 'Inter', sans-serif;
            color: #fff;
            overflow: hidden;
            -webkit-user-select: none;
            user-select: none;
            -webkit-tap-highlight-color: transparent;
            position: relative;
        }

        .sequence-screen-layout {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            text-align: center;
            padding: 20px;
            box-sizing: border-box;
            width: 100%;
            height: 100%;
        }

        .title {
            font-size: 2.2rem;
            margin-bottom: 2rem;
            text-transform: uppercase;
            letter-spacing: 3px;
            font-weight: 900;
            background: linear-gradient(90deg, #00e1ff, #b300ff);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
        }

        .btn-start {
            width: 80%;
            max-width: 300px;
            padding: 22px;
            border-radius: 50px;
            background: linear-gradient(135deg, rgba(30, 30, 50, 0.9), rgba(15, 15, 25, 0.95));
            border: 4px solid var(--pad3);
            box-shadow: 0 10px 30px rgba(0,0,0,0.8), inset 0 0 20px rgba(0, 225, 255, 0.3);
            color: #fff;
            font-size: 1.5rem;
            font-weight: bold;
            cursor: pointer;
            transition: transform 0.1s;
        }

        .btn-start:active {
            transform: scale(0.95);
            background: var(--pad3);
            color: #000;
        }

        .wait-text {
            font-size: 1.5rem;
            color: #aaaaaa;
            animation: pulse-text 1.5s infinite alternate;
            font-weight: 600;
            max-width: 400px;
        }

        @keyframes pulse-text {
            0% { opacity: 0.4; }
            100% { opacity: 1; }
        }

        .grid-container {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            grid-gap: 15px;
            width: 95vw;
            max-width: 500px;
            height: 60vw;
            max-height: 330px;
        }

        .pad {
            border-radius: 20px;
            background: rgba(20, 20, 25, 1);
            border: 4px solid;
            transition: filter 0.1s, transform 0.1s;
            cursor: pointer;
            position: relative;
            box-shadow: inset 0 0 20px rgba(0,0,0,0.5);
        }

        .pad-0 { border-color: rgba(0, 255, 65, 0.4); }
        .pad-1 { border-color: rgba(255, 0, 60, 0.4); }
        .pad-2 { border-color: rgba(255, 251, 0, 0.4); }
        .pad-3 { border-color: rgba(0, 225, 255, 0.4); }
        .pad-4 { border-color: rgba(179, 0, 255, 0.4); }
        .pad-5 { border-color: rgba(255, 136, 0, 0.4); }

        .pad.tapped {
            transform: scale(0.93);
        }

        .pad-0.tapped { background: var(--pad0); box-shadow: 0 0 40px var(--pad0); border-color: #fff; }
        .pad-1.tapped { background: var(--pad1); box-shadow: 0 0 40px var(--pad1); border-color: #fff; }
        .pad-2.tapped { background: var(--pad2); box-shadow: 0 0 40px var(--pad2); border-color: #fff; }
        .pad-3.tapped { background: var(--pad3); box-shadow: 0 0 40px var(--pad3); border-color: #fff; }
        .pad-4.tapped { background: var(--pad4); box-shadow: 0 0 40px var(--pad4); border-color: #fff; }
        .pad-5.tapped { background: var(--pad5); box-shadow: 0 0 40px var(--pad5); border-color: #fff; }

        .status-msg {
            margin-top: 30px;
            font-size: 1.4rem;
            font-weight: 800;
            height: 30px;
            text-transform: uppercase;
        }

        .connection-indicator {
            position: absolute;
            top: 20px;
            right: 20px;
            font-size: 0.8rem;
            padding: 3px 10px;
            border-radius: 12px;
            font-weight: bold;
            z-index: 100;
        }
    `;

    // Conectar WebSocket
    useEffect(() => {
        const connectWS = async () => {
            const overlayType = `sequence:player:${screenId}`;
            await ConnectWebSocketForOverlay(
                overlayType,
                (status) => setConnectionStatus(status),
                (toastMsg) => console.log(`[SequenceFan WS] ${toastMsg}`)
            );
            SetSocketMessageHandler(handleIncomingMessage);
        };

        connectWS();

        // Cargar estado inicial por HTTP
        fetchInitialState();

        return () => {
            ClearSocketMessageHandler();
            DisconnectWebSocket();
        };
    }, [screenId]);

    const fetchInitialState = async () => {
        const res = await GetState(screenId);
        if (res && res.success && res.data) {
            setGameState(res.data);
            lastPhaseRef.current = res.data.phase;
        }
    };

    const handleIncomingMessage = (message) => {
        if (!message || !message.type) return;

        const type = message.type.toLowerCase();
        const payloadData = message.payload || {};

        if (type === 'sequence.layout' || type === 'sequence.update') {
            setGameState(payloadData);

            // Manejo de vibraciones hápticas según cambio de fase
            const prevPhase = lastPhaseRef.current;
            const newPhase = payloadData.phase;

            if (prevPhase !== newPhase) {
                lastPhaseRef.current = newPhase;

                if (newPhase === 'PLAYER_INPUT') {
                    // Turno del aficionado (desbloqueado) -> Vibración doble corta
                    if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
                } else if (newPhase === 'WON') {
                    // Ganó -> Vibración larga
                    if (navigator.vibrate) navigator.vibrate(500);
                } else if (newPhase === 'LOST') {
                    // Perdió -> Glitch error
                    if (navigator.vibrate) navigator.vibrate([300, 100, 300, 100, 500]);
                }
            }
        } else if (type === 'sequence.reset') {
            setGameState({
                phase: 'WAITING',
                sequence: [],
                playerStep: 0,
                isPlaying: false,
                matchId: ''
            });
            lastPhaseRef.current = 'WAITING';
        }
    };

    const handleStartGame = async () => {
        if (isSending) return;
        setIsSending(true);
        if (navigator.vibrate) navigator.vibrate(50);
        
        const res = await StartSequence({
            screenId,
            sequenceLength: 7,
            playbackSpeedMs: 500
        });

        if (!res.success) {
            showToast('Error al iniciar la secuencia. Intenta de nuevo.');
        }
        setIsSending(false);
    };

    const handlePadTap = async (colorId) => {
        if (gameState.phase !== 'PLAYER_INPUT' || isSending) return;
        
        // Efecto visual instantáneo y vibración
        setTappedPad(colorId);
        if (navigator.vibrate) navigator.vibrate(30);

        setTimeout(() => {
            setTappedPad(null);
        }, 150);

        setIsSending(true);
        const res = await RegisterTap(screenId, gameState.matchId, colorId);
        if (!res.success) {
            showToast('Error al registrar toque.');
        }
        setIsSending(false);
    };

    const showToast = (msg) => {
        setToastMessage(msg);
        setTimeout(() => setToastMessage(''), 3000);
    };

    const phase = gameState.phase;

    return (
        <div className="sequence-fan-body">
            <style>{customStyles}</style>

            {/* Connection status badge */}
            <span className="connection-indicator" style={{
                backgroundColor: connectionStatus === 'connected' ? '#198754' : '#dc3545'
            }}>
                {connectionStatus === 'connected' ? 'En línea' : 'Desconectado'}
            </span>

            <img src="/img/page/adsl-five.png" style={{ position: 'fixed', top: '20px', left: '50%', transform: 'translateX(-50%)', width: '300px' }} />

            {/* View 1: WAITING */}
            {phase === 'WAITING' && (
                <div className="sequence-screen-layout">
                    {/* <img src="/img/page/adsl-five.png" alt="Logo" className="LogoADSLxZtudia mb-4" style={{ maxWidth: '280px' }} />
                    <div className="title">Secuencias</div> */}
                    <div className="wait-text">
                        ⏳ Esperando a que inicie la partida...
                    </div>
                </div>
            )}

            {/* View 2: PLAYBACK */}
            {phase === 'PLAYBACK' && (
                <div className="sequence-screen-layout">
                    <div className="wait-text">
                        ⚠️ Mira a la pantalla principal y memoriza la secuencia...
                    </div>
                </div>
            )}

            {/* View 3: PLAYER_INPUT / WON / LOST */}
            {(phase === 'PLAYER_INPUT' || phase === 'WON' || phase === 'LOST') && (() => {
                const DEFAULT_BUTTONS = [
                    { id: 0, color: '#00ff41', imageUrl: '', label: 'VERDE' },
                    { id: 1, color: '#ff003c', imageUrl: '', label: 'ROJO' },
                    { id: 2, color: '#fffb00', imageUrl: '', label: 'AMARILLO' },
                    { id: 3, color: '#00e1ff', imageUrl: '', label: 'AZUL' },
                    { id: 4, color: '#b300ff', imageUrl: '', label: 'MORADO' },
                    { id: 5, color: '#ff8800', imageUrl: '', label: 'NARANJA' }
                ];
                const buttonsToUse = (gameState.buttons && gameState.buttons.length > 0) ? gameState.buttons : DEFAULT_BUTTONS;

                const getMediaUrl = (path) => {
                    if (!path) return '';
                    if (path.startsWith('http://') || path.startsWith('https://')) return path;
                    const apiBase = (process.env.REACT_APP_API_URL || '').replace('/api/', '/').replace('/api', '/');
                    return `${apiBase}${path}`;
                };

                const gridStyle = {
                    display: 'grid',
                    gridTemplateColumns: buttonsToUse.length <= 4 ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
                    gridGap: '15px',
                    width: '95vw',
                    maxWidth: '500px',
                    height: '60vw',
                    maxHeight: '330px'
                };

                return (
                    <div className="sequence-screen-layout">
                        <div className="title" style={{ fontSize: '1.2rem', marginBottom: '20px' }}>
                            {phase === 'PLAYER_INPUT' ? '¡Repite la secuencia!' : 'Resultado'}
                        </div>

                        <div style={gridStyle}>
                            {buttonsToUse.map((btn) => {
                                const isTapped = tappedPad === btn.id;
                                const padStyle = {
                                    borderRadius: '20px',
                                    background: isTapped ? btn.color : 'rgba(20, 20, 25, 1)',
                                    border: '4px solid',
                                    borderColor: isTapped ? '#fff' : `${btn.color}66`, // 66 is ~40% opacity in hex
                                    boxShadow: isTapped ? `0 0 40px ${btn.color}` : 'inset 0 0 20px rgba(0,0,0,0.5)',
                                    transform: isTapped ? 'scale(0.93)' : 'none',
                                    transition: 'filter 0.1s, transform 0.1s',
                                    cursor: 'pointer',
                                    position: 'relative',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    overflow: 'hidden'
                                };
                                return (
                                    <div 
                                        key={btn.id} 
                                        style={padStyle}
                                        onPointerDown={() => handlePadTap(btn.id)}
                                    >
                                        {btn.imageUrl && (
                                            <img 
                                                src={getMediaUrl(btn.imageUrl)} 
                                                alt={btn.label} 
                                                style={{ 
                                                    width: '110%', //'100%', 
                                                    height: '120%', //'100%',
                                                    objectFit: 'cover',
                                                    opacity: isTapped ? 1 : 0.6
                                                }} 
                                            />
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        <div className="status-msg" style={{
                            color: phase === 'WON' ? 'var(--pad0)' : phase === 'LOST' ? 'var(--pad1)' : '#fff'
                        }}>
                            {phase === 'WON' && '¡SECUENCIA CORRECTA!'}
                            {phase === 'LOST' && '¡INCORRECTO!'}
                            {phase === 'PLAYER_INPUT' && `Paso ${gameState.playerStep} de ${gameState.sequence.length}`}
                        </div>
                    </div>
                );
            })()}

            {/* Toast Alerts */}
            {toastMessage && (
                <div style={{
                    position: 'absolute', bottom: '20px', left: '50%', transform: 'translateX(-50%)',
                    backgroundColor: '#198754', color: 'white', padding: '8px 16px', borderRadius: '20px',
                    fontWeight: 'bold', fontSize: '0.9rem', boxShadow: '0 4px 10px rgba(0,0,0,0.5)', zIndex: 200
                }}>
                    {toastMessage}
                </div>
            )}

            <p className='FooterCredits' style={{ position: 'fixed', bottom: '0', left: '50%', transform: 'translateX(-50%)', fontSize: '20px', color: '#fff' }}>Hecho con ♥ por Interaktia | 2026</p>
        </div>
    );
};

export { SequenceFan };
